import React, { useRef, useState } from "react";

export const REPLAY_SPEEDS = [1, 2, 5, 10, 20, 50, 100, 200];
export const REPLAY_WINDOWS = [24, 48, 72];

const pad = (value) => String(value).padStart(2, "0");

function formatMoment(time, now) {
  const date = new Date(time);
  const today = new Date(now);
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (date.toDateString() === today.toDateString()) return clock;
  return `${pad(date.getMonth() + 1)}.${pad(date.getDate())}. ${clock}`;
}

// Idővonal a canvas fölött: jobb széle a jelen, bal széle az ablak kezdete.
// A jelölő húzásával múltbeli pillanatra lehet ugrani; elengedéskor indul a lejátszás.
const LiveTimeline = ({
  windowStart,
  now,
  replayTime = null,
  speed = 1,
  windowHours = 24,
  onSeek = () => {},
  onSeekEnd = () => {},
  onSpeedChange = () => {},
  onWindowChange = () => {},
  onLive = () => {},
  expanded = true,
  onExpand = () => {},
}) => {
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [menu, setMenu] = useState(null); // "speed" | "window" | null
  const span = Math.max(now - windowStart, 1);
  const shownTime = replayTime ?? now;
  const ratio = Math.min(1, Math.max(0, (shownTime - windowStart) / span));
  const live = replayTime === null;

  const timeAt = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const position = Math.min(1, Math.max(0, (clientX - rect.left) / Math.max(rect.width, 1)));
    return windowStart + position * span;
  };

  const handlePointerDown = (event, onHandle) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { startX: event.clientX, moved: !onHandle, onHandle };
    setMenu(null);
    if (!onHandle) onSeek(timeAt(event.clientX));
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.moved && Math.abs(event.clientX - drag.startX) < 4) return;
    drag.moved = true;
    onSeek(timeAt(event.clientX));
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.onHandle && !drag.moved) {
      setMenu((current) => (current === "speed" ? null : "speed"));
      return;
    }
    onSeekEnd(timeAt(event.clientX));
  };

  // Összecsukva csak a Live gomb látszik; rákattintva nyílik ki a csík.
  if (!expanded) {
    return (
      <button
        type="button"
        aria-label="Open timeline"
        onClick={onExpand}
        className="pointer-events-auto rounded-b-xl border-x border-b border-slate-800 bg-slate-900 px-4 pb-1.5 pt-1 text-[0.65rem] font-bold text-teal-300 shadow-md hover:text-teal-200"
      >
        ● Live
      </button>
    );
  }

  return (
    <div className="pointer-events-auto relative flex items-center gap-2 rounded-b-xl border-x border-b border-slate-800 bg-slate-900 px-3 pb-2 pt-5 text-[0.65rem] text-slate-200 shadow-md">
      <button
        type="button"
        aria-label="Time window"
        onClick={() => setMenu((current) => (current === "window" ? null : "window"))}
        className="shrink-0 rounded-md border border-slate-600 px-1.5 py-0.5 font-semibold text-slate-300 hover:border-teal-400 hover:text-teal-300"
      >
        −{windowHours ? `${windowHours} h` : formatMoment(windowStart, now)}
      </button>

      <div
        ref={trackRef}
        data-testid="timeline-track"
        className="relative h-6 w-72 cursor-pointer md:w-96"
        onPointerDown={(event) => handlePointerDown(event, false)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-700" />
        <div
          className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-teal-500/70"
          style={{ width: `${ratio * 100}%` }}
        />
        <div
          role="slider"
          aria-label="Displayed time"
          aria-valuemin={windowStart}
          aria-valuemax={now}
          aria-valuenow={Math.round(shownTime)}
          aria-valuetext={formatMoment(shownTime, now)}
          tabIndex={0}
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-grab"
          style={{ left: `${ratio * 100}%` }}
          onPointerDown={(event) => { event.stopPropagation(); handlePointerDown(event, true); }}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div className={`h-4 w-4 rounded-full border-2 ${live ? "border-teal-300 bg-teal-500" : "border-amber-200 bg-amber-400"}`} />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 font-semibold text-slate-100">
            {formatMoment(shownTime, now)}{!live && speed !== 1 ? ` · ${speed}×` : ""}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Back to live"
        onClick={onLive}
        className={`shrink-0 rounded-md px-1.5 py-0.5 font-bold ${
          live ? "text-teal-300" : "border border-teal-400 text-teal-200 hover:bg-teal-500/15"
        }`}
      >
        {live ? "● Live" : "Live"}
      </button>

      {menu === "speed" && (
        <div className="absolute left-1/2 top-full mt-1 flex -translate-x-1/2 gap-1 rounded-lg border border-slate-700 bg-slate-900 p-1 shadow-xl">
          {REPLAY_SPEEDS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => { onSpeedChange(value); setMenu(null); }}
              className={`rounded px-2 py-1 font-semibold ${value === speed ? "bg-teal-500 text-slate-950" : "text-slate-300 hover:bg-slate-800"}`}
            >
              {value}×
            </button>
          ))}
        </div>
      )}

      {menu === "window" && (
        <div className="absolute left-0 top-full mt-1 rounded-lg border border-slate-700 bg-slate-900 p-2 shadow-xl">
          <div className="mb-1 flex gap-1">
            {REPLAY_WINDOWS.map((hours) => (
              <button
                key={hours}
                type="button"
                onClick={() => { onWindowChange({ hours }); setMenu(null); }}
                className={`rounded px-2 py-1 font-semibold ${hours === windowHours ? "bg-teal-500 text-slate-950" : "text-slate-300 hover:bg-slate-800"}`}
              >
                {hours} h
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-slate-400">
            <span>From</span>
            <input
              type="date"
              aria-label="Start date"
              onChange={(event) => {
                if (!event.target.value) return;
                const [year, month, day] = event.target.value.split("-").map(Number);
                onWindowChange({ start: new Date(year, month - 1, day).getTime() });
                setMenu(null);
              }}
              className="rounded border border-slate-600 bg-slate-950 px-1.5 py-0.5 text-slate-100"
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default LiveTimeline;
